import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppStore } from '../lib/store'
import { toast } from '../lib/toast'
import { X, Zap, User as UserIcon, Lock, Mail, Phone, MapPin, FileText, Home, Users, Shield, Eye, EyeOff, CheckCircle } from 'lucide-react'
import type { User as UserType } from '../types'

interface AuthModalProps {
  isOpen: boolean
  onClose: () => void
  mode: 'login' | 'signup'
  initialRole?: 'client' | 'provider' | 'admin'
  onModeChange?: (mode: 'login' | 'signup') => void
}

export default function AuthModal({ isOpen, onClose, mode, initialRole, onModeChange }: AuthModalProps) {
  const navigate = useNavigate()
  const { setUser } = useAppStore()
  const [showPassword, setShowPassword] = React.useState(false)
  const [currentMode, setCurrentMode] = React.useState<'login' | 'signup'>(mode)
  const [formData, setFormData] = React.useState({
    email: '',
    password: '',
    name: '',
    role: initialRole || 'client' as 'client' | 'provider' | 'admin'
  })
  const [loading, setLoading] = React.useState(false)

  React.useEffect(() => {
    setCurrentMode(mode)
  }, [mode])

  const handleModeSwitch = (newMode: 'login' | 'signup') => {
    setCurrentMode(newMode)
    if (onModeChange) {
      onModeChange(newMode)
    }
  }

  // Demo users for testing
  const demoUsers = {
    client: {
      email: 'demo_client@smartclean.com',
      password: 'client123',
      name: 'Demo Client',
      role: 'client' as const
    },
    provider: {
      email: 'demo_provider@smartclean.com', 
      password: 'provider123',
      name: 'Demo Provider',
      role: 'provider' as const
    },
    admin: {
      email: 'demo_admin@smartclean.com',
      password: 'admin123', 
      name: 'Demo Admin',
      role: 'admin' as const
    }
  }

  const handleDemoLogin = (role: 'client' | 'provider' | 'admin') => {
    const demoUser = demoUsers[role]
    setFormData({
      email: demoUser.email,
      password: demoUser.password,
      name: demoUser.name,
      role: demoUser.role
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    // Simulate authentication
    setTimeout(() => {
      const mockUser: UserType = {
        id: Date.now().toString(),
        email: formData.email,
        role: formData.role,
        name: formData.name,
        status: 'active',
        created_at: new Date().toISOString(),
        phone: '',
        address: '',
        total_jobs: 0,
        total_spent: 0
      }
      
      // Set user first, then close modal, then navigate
      setUser(mockUser)
      setLoading(false)
      
      // Small delay to ensure state is set
      setTimeout(() => {
        onClose()
        navigate('/dashboard')
      }, 100)
    }, 1500)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 dark:from-gray-800 dark:to-gray-900 rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto border border-green-100 dark:border-gray-700">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-green-200 dark:border-gray-700 bg-gradient-to-r from-green-100/90 via-emerald-100/90 to-teal-100/90 dark:from-gray-800 dark:to-gray-900 backdrop-blur-md">
          <h2 className="text-xl font-bold bg-gradient-to-r from-green-700 to-emerald-600 bg-clip-text text-transparent">
            {currentMode === 'login' ? 'Welcome Back' : 'Create Account'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg transition-colors hover:bg-gray-200/50 dark:hover:bg-gray-700/50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Demo Users Section - Only in Development */}
          {mode === 'login' && (
            <div className="bg-white/50 dark:bg-gray-800/50 border border-green-200 dark:border-gray-600 rounded-lg p-4 backdrop-blur-sm">
              <div className="flex items-center mb-3">
                <Zap className="w-4 h-4 text-emerald-600 mr-2" />
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Quick Demo Login</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => handleDemoLogin('client')}
                  className="p-2 bg-white/70 dark:bg-gray-700/50 border border-green-300 dark:border-gray-600 rounded hover:bg-green-100/50 dark:hover:bg-gray-600/50 transition-colors text-xs backdrop-blur-sm"
                >
                  <Home className="w-4 h-4 mx-auto mb-1 text-emerald-600" />
                  <div className="font-medium text-gray-900 dark:text-gray-100">Client</div>
                </button>
                <button
                  type="button"
                  onClick={() => handleDemoLogin('provider')}
                  className="p-2 bg-white/70 dark:bg-gray-700/50 border border-green-300 dark:border-gray-600 rounded hover:bg-green-100/50 dark:hover:bg-gray-600/50 transition-colors text-xs backdrop-blur-sm"
                >
                  <Users className="w-4 h-4 mx-auto mb-1 text-emerald-600" />
                  <div className="font-medium text-gray-900 dark:text-gray-100">Provider</div>
                </button>
                <button
                  type="button"
                  onClick={() => handleDemoLogin('admin')}
                  className="p-2 bg-white/70 dark:bg-gray-700/50 border border-green-300 dark:border-gray-600 rounded hover:bg-green-100/50 dark:hover:bg-gray-600/50 transition-colors text-xs backdrop-blur-sm"
                >
                  <Shield className="w-4 h-4 mx-auto mb-1 text-emerald-600" />
                  <div className="font-medium text-gray-900 dark:text-gray-100">Admin</div>
                </button>
              </div>
            </div>
          )}

          {/* Role Selection */}
          {mode === 'signup' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-3">
                Choose Your Role
              </label>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, role: 'client' }))}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    formData.role === 'client'
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                      : 'border-gray-200 dark:border-gray-500 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-400'
                  }`}
                >
                  <Home className="w-5 h-5 mx-auto mb-1" />
                  <span className="text-xs font-medium">Client</span>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, role: 'provider' }))}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    formData.role === 'provider'
                      ? 'border-green-500 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                      : 'border-gray-200 dark:border-gray-500 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-400'
                  }`}
                >
                  <Users className="w-5 h-5 mx-auto mb-1" />
                  <span className="text-xs font-medium">Provider</span>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, role: 'admin' }))}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    formData.role === 'admin'
                      ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                      : 'border-gray-200 dark:border-gray-500 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-400'
                  }`}
                >
                  <Shield className="w-5 h-5 mx-auto mb-1" />
                  <span className="text-xs font-medium">Admin</span>
                </button>
              </div>
            </div>
          )}

          {mode === 'signup' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Full Name
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-green-300 dark:border-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="Enter your full name"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              Email Address
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="w-full pl-10 pr-3 py-2 border border-green-300 dark:border-gray-500 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 dark:focus:ring-emerald-400 dark:focus:border-emerald-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="Enter your email"
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-green-300 dark:border-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-400 pr-10 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="Enter your password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-100"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="remember"
                className="h-4 w-4 text-emerald-600 border-gray-300 dark:border-gray-500 rounded focus:ring-emerald-500 dark:focus:ring-emerald-400 bg-white dark:bg-gray-700"
              />
              <label htmlFor="remember" className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                Remember me
              </label>
            </div>
            <a 
              href="#" 
              className="text-sm text-orange-600 hover:text-orange-500 dark:text-orange-400 dark:hover:text-orange-300"
              onClick={(e) => {
                e.preventDefault()
                handleModeSwitch(currentMode === 'login' ? 'signup' : 'login')
              }}
            >
              {currentMode === 'login' ? 'Forgot password?' : 'Already have an account?'}
            </a>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-green-700 text-white py-2 px-4 rounded-lg hover:bg-green-800 focus:ring-4 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
          >
            {loading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-r-2 border-white"></div>
            ) : (
              <>
                {currentMode === 'login' ? 'Sign In' : 'Sign Up'}
                <CheckCircle className="ml-2 w-4 h-4" />
              </>
            )}
          </button>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-dark-border rounded-b-xl">
          <p className="text-xs text-gray-600 dark:text-dark-text-secondary text-center">
            By {currentMode === 'login' ? 'signing in' : 'creating an account'}, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </div>
    </div>
  )
}
